import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { environment } from '../config/environment';
import { auth } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { createApiClient } from './api-client.unified';

// Mock handlers registry
const mockHandlers: Record<string, (params?: URLSearchParams) => Promise<any>> = {};

// Register a mock handler for a specific endpoint
export const registerMockHandler = (endpoint: string, handler: (params?: URLSearchParams) => Promise<any>) => {
    mockHandlers[endpoint] = handler;
};

// Helper to get base endpoint without query params
const getBaseEndpoint = (url: string | undefined): string => {
    if (!url) return '';
    const [baseUrl] = url.split('?');
    return baseUrl.replace(environment.ctLiteApiUrl, '').replace(environment.expressApiUrl, '');
};

export const useApiClient = () => {
    const { token, logout, setToken } = useAuth();
    const apiClientRef = useRef<AxiosInstance | null>(null);
    const isRefreshing = useRef(false);
    const failedQueue: { resolve: (value: any) => void; reject: (error: any) => void; config: any }[] = [];
    const navigate = useNavigate();

    const processQueue = (error: any) => {
        failedQueue.forEach((prom) => {
            if (!error) {
                prom.config.headers.Authorization = `Bearer ${token}`;
                prom.resolve(axios(prom.config));
            } else {
                prom.reject(error);
            }
        });
        failedQueue.length = 0;
    };

    const refreshToken = async () => {
        const user = auth.currentUser;
        if (!user) throw new Error('No user logged in');

        const newToken = await user.getIdToken(true);
        setToken(newToken);
        return newToken;
    };

    // Create the apiClient instance once
    if (!apiClientRef.current) {
        const client = createApiClient();

        // Add mock interceptor if mock mode is enabled
        if (environment.useMockApi) {
            client.interceptors.request.use(async (config) => {
                const endpoint = getBaseEndpoint(config.url);
                if (endpoint && mockHandlers[endpoint]) {
                    let params: URLSearchParams | undefined;
                    if (config.params instanceof URLSearchParams) {
                        params = config.params;
                    } else if (config.params) {
                        params = new URLSearchParams();
                        Object.entries(config.params).forEach(([key, value]) => {
                            if (Array.isArray(value)) {
                                if (value.length > 0) {
                                    value.forEach((v) => params?.append(key, v.toString()));
                                }
                            } else if (value !== undefined && value !== null && value !== '') {
                                params?.append(key, value.toString());
                            }
                        });
                    }

                    if (params && Array.from(params.entries()).length === 0) {
                        params = undefined;
                    }

                    console.log('Mock API Request:', {
                        endpoint,
                        params: params ? Object.fromEntries(params.entries()) : 'none',
                    });

                    config.adapter = async () => {
                        const response = await mockHandlers[endpoint](params);
                        return {
                            data: response,
                            status: 200,
                            statusText: 'OK',
                            headers: {},
                            config,
                        };
                    };
                }
                return config;
            });
        }

        apiClientRef.current = client;
    }

    useEffect(() => {
        const apiClient = apiClientRef.current;
        if (!apiClient) return;

        // Attach request interceptor
        const requestInterceptor = apiClient.interceptors.request.use(
            async (config) => {
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Attach response interceptor to handle 401 errors
        const responseInterceptor = apiClient.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (isRefreshing.current) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject, config: originalRequest });
                        });
                    }

                    originalRequest._retry = true;
                    isRefreshing.current = true;

                    try {
                        const user = auth.currentUser;
                        if (user) {
                            const newToken = await refreshToken();

                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            processQueue(null);

                            return apiClient(originalRequest); // Retry the failed request
                        } else {
                            throw new Error('No user logged in');
                        }
                    } catch (refreshError) {
                        processQueue(refreshError);
                        logout();
                        navigate('/login'); // Redirect to login page
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshing.current = false;
                    }
                } else if (error.response?.status === 403) {
                    const logoutError = new Error('Logged out');
                    processQueue(logoutError);
                    logout();
                    navigate('/login');
                    return Promise.reject(logoutError);
                }

                return Promise.reject(error);
            }
        );

        // Cleanup interceptors on unmount
        return () => {
            apiClient.interceptors.request.eject(requestInterceptor);
            apiClient.interceptors.response.eject(responseInterceptor);
        };
    }, [token, logout, navigate, setToken]);

    return apiClientRef.current;
};
